import { Grid, Paper, Typography, useMediaQuery } from "@mui/material";
import AcxNoAccessMediaPlayer from "SvgIcons/AcxNoAccessMediaPlayer";
import ConversationsStore, {
    loadAgentCoaching,
    loadConversation,
    loadCurrentEval,
    SidePanelType,
} from "components/Conversations/Stores/ConversationsStore";
import { AgentActionButton } from "components/KBo/Conversation/Actions/AgentActionButton";
import { Routes } from "components/Navigation/Routes";
import { initialZoomLevelsArray } from "components/SoundClipEditor/Stores/SoundClipEditorStore";
import AcxButton from "components/UI/AcxButton";
import AcxEmptyState from "components/UI/AcxEmptyStateIndicator/AcxEmptyState";
import SuccessDialog from "components/UI/AcxSuccessDialog";
import VerticalDrawerContentTemplate from "components/UI/Drawer/VerticalDrawerContentTemplate";
import { observer } from "mobx-react";
import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useStore } from "utils/useStore";
import EvaluateConversationDialog from "../EvaluateConversationDialog";
import FocusedConversationTabs from "./FocusedConversationTabs";
import ConvoWorkflowContainer from "./components/convoSidePanel/ConvoWorkflowContainer";
import LabelConversationDialog from "../LabelConversationDialog";
import {
    defaultDrawerOffset,
    LayoutDrawerStore,
} from "stores/Layout/LayoutDrawerStore";
import ConversationLabelingStore from "./components/convoSidePanel/store/ConversationLabelingStore";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import theme from "Theme/AppTheme";
import TranscriptionLinkedSoundClipEditor from "components/UI/AcxTranscription/TranscriptionLinkedSoundClipEditor";
import { useCurrentChatStore } from "components/KBo/Stores/useCurrentChat";
import ConversationLabelingContainer from "./components/convoSidePanel/ConversationLabelingContainer";
import { FavoriteToggle } from "components/UI/FavoriteToggle";
import ConvoEvalReview from "components/Agents/EvalReview/ConvoEvalReview";
import FlagRoundedIcon from "@mui/icons-material/FlagRounded";
import { AgentCoachingStatus } from "models/AgentModels/AgentCoaching";
import { EvaluationStatus } from "models/Evaluation";
import { alreadyInDraftMessage } from "components/Agents/Dashboard/Stores/Helpers";
import { CoachingLockStatus } from "components/Agents/EvalReview/Stores/EvalReviewStore";
import { emptyGUID } from "components/Admin/Organizations/OrganizationDetail/OrganizationModules/OrganizationModuleDetails/OrganizationModuleQuestionDependencies";

type possibleRoutes = "workflow" | typeof Routes.EVAL_COACHING | "";

type Props = { currentRoute: possibleRoutes };

const transitionDuration = 500;

const FocusedConversation: React.FunctionComponent<Props> = observer(
    ({ currentRoute }) => {
        const isLargeScreen = useMediaQuery("(min-width:1536px)");

        const drawerStore = useStore(LayoutDrawerStore);
        const store = useStore(ConversationsStore);
        const currentChatStore = useCurrentChatStore();
        const convoLabelingStore = useStore(ConversationLabelingStore);
        const messageStore = useStore(MessageStore);

        const isWorkflowRoute = currentRoute === "workflow";

        // transition examples here: https://v5.mui.com/material-ui/react-drawer/
        const containerTransitionStyles = {
            transition: theme.transitions.create("margin", {
                easing: theme.transitions.easing.sharp,
                duration: transitionDuration,
            }),
            marginRight: `0px`,
            ...(!!drawerStore.isOpen && {
                transition: theme.transitions.create("margin", {
                    easing: theme.transitions.easing.easeOut,
                    duration: transitionDuration,
                }),
                marginRight: drawerStore?.width ?? 20,
            }),
        };

        // re-init peaks when opening/closing the side panel so that the waveform properly resizes
        useEffect(() => {
            setTimeout(() => {
                // need to ensure that the audio element has been initialized before attempting to init peaks
                if (store.soundClipEditorStore?.audioElement) {
                    store.soundClipEditorStore?.initializePeaks(
                        initialZoomLevelsArray,
                    );
                }
            }, transitionDuration + 100);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [store.sidePanel]);

        const navigate = useNavigate();

        let { id } = useParams();

        useEffect(() => {
            if (!store.selectedConversationId) {
                store.setSelectedConversationId(id);
            }
            if (isWorkflowRoute) {
                store.setSidePanel(SidePanelType.Workflow);
            }

            return () => {
                store.setSelectedConversationId(undefined);
                store.setSidePanel(SidePanelType.Hidden);
            };
        }, [id, isWorkflowRoute, store, store.selectedConversationId]);

        useEffect(() => {
            if (currentRoute === Routes.EVAL_COACHING) {
                store.openCoachingPanelWithLock();
            }
            return () => {
                // Don't wipe the coaching store while the panel is still open.
                // Saving a draft flips currentUserIsDraftOwner, which re-runs
                // this effect; (AUT-10104)
                if (store.sidePanel !== SidePanelType.Coaching) {
                    store.evalReviewStore.reset();
                }
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            currentRoute,
            store.currentEval,
            store.currentUserIsDraftOwner,
            store.selectedConversation?.agentId,
            store.agentCoaching,
            store.coachingContextLoading,
        ]);

        useEffect(() => {
            if (!store.handleUnauthorizedError) {
                store.handleUnauthorizedError = (e) => {
                    navigate(Routes.HOME, { replace: true });
                };
            }
            return () => {
                store.handleUnauthorizedError = undefined;
            };
        }, [navigate, store]);

        useEffect(() => {
            if (
                !store.selectedConversationId ||
                store.selectedConversation?.isChat
            )
                return;
            store.setSoundClipEditorStore();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [store.selectedConversationId, store.selectedConversation?.isChat]);

        const showSoundClipEditor =
            !store.selectedConversation?.isChat &&
            store.selectedConversation?.callDurationMillis !== 0;

        const canSample =
            store.authStore.canUserEdit("Sample Single Conversation") &&
            !store.selectedConversation?.evalType;

        const showLabelButton =
            (store.authStore.canUserView("Data Labeling") ||
                store.authStore.canUserView("Research")) &&
            !isWorkflowRoute;

        const showSafetyEventButton =
            store.authStore.canUserView("Safety Event Review") &&
            !isWorkflowRoute &&
            convoLabelingStore.conversationSafetyEventLicensedModules.length >
                0;

        const hasAgent =
            !!store.selectedConversation?.agentId &&
            store.selectedConversation.agentId !== emptyGUID;

        const hasEvalAndIsCoachable =
            store.selectedConversation?.evaluationId &&
            (store.currentEval?.evaluationStatus ===
                EvaluationStatus.Completed ||
                store.currentEval?.evaluationStatus ===
                    EvaluationStatus.Disputed ||
                store.currentEval?.evaluationStatus ===
                    EvaluationStatus.Escalated);

        const hasNoEvalButHasAgent =
            !store.selectedConversation?.evaluationId && hasAgent;

        const coachingEligibilityLoading =
            hasAgent &&
            (store.getTaskLoading(loadCurrentEval) ||
                store.getTaskLoading(loadAgentCoaching));

        const showCoachButton =
            !store.authStore.isLoggedInUserAgent() &&
            (store.authStore.canUserEdit("Agent Coaching") ||
                store.authStore.isUserUltra()) &&
            hasAgent &&
            (coachingEligibilityLoading ||
                hasEvalAndIsCoachable ||
                hasNoEvalButHasAgent);

        const labelButtonIsDisabled = store.sidePanel !== SidePanelType.Hidden;

        const coachingInDraft =
            store.currentAgentCoaching?.coachingStatus ===
            AgentCoachingStatus.InProgress;

        const hasBeenCoached =
            store.currentAgentCoaching?.coachingStatus ===
            AgentCoachingStatus.Completed;

        const openDrawerAndSetContent = useCallback(
            (content: ReactNode, hideClose: boolean, title?: string) => {
                drawerStore.closeAndResetDrawer();
                drawerStore.restoreDefaults();
                drawerStore.setCustomRootStyles({
                    zIndex: theme.zIndex.appBar - 1,
                });
                drawerStore.setOffsetPixels(defaultDrawerOffset);
                drawerStore.setVariant("persistent");
                drawerStore.setTransitionDuration(transitionDuration);

                drawerStore.setContentFactory(() => (
                    <VerticalDrawerContentTemplate
                        content={content}
                        title={title}
                        width={`${drawerStore.width}px`}
                        contentStyles={{ padding: 0, flex: 1 }}
                        hideCloseIcon={hideClose}
                        onClose={() => store.setSidePanel(SidePanelType.Hidden)}
                        footerButtons={drawerStore.footerButtons}
                    />
                ));
                drawerStore.setOpen(true);
            },
            [drawerStore, store],
        );

        useEffect(() => {
            const handleUnload = () => {
                if (store.sidePanel === SidePanelType.DataLabeling) {
                    convoLabelingStore.ReleaseDataLabelingLock();
                }
                if (
                    store.sidePanel === SidePanelType.Coaching ||
                    store.evalReviewStore.coachingLockStatus ===
                        CoachingLockStatus.Acquiring
                ) {
                    store.evalReviewStore.releaseCoachingLock();
                }
            };

            window.addEventListener("pagehide", handleUnload);
            return () => {
                window.removeEventListener("pagehide", handleUnload);
            };
        }, [convoLabelingStore, store.sidePanel, store.evalReviewStore]);

        // Release the coaching lock whenever the coaching panel is closed
        // (X, Cancel, Complete, or switching panels).
        useEffect(() => {
            if (
                store.sidePanel !== SidePanelType.Coaching &&
                store.evalReviewStore.hasCoachingLock
            ) {
                store.evalReviewStore.releaseCoachingLock();
            }
        }, [store.sidePanel, store.evalReviewStore]);

        useEffect(() => {
            if (store.sidePanel === SidePanelType.DataLabeling) {
                openDrawerAndSetContent(
                    <ConversationLabelingContainer />,
                    true,
                    convoLabelingStore.selectedLicensedModule?.name,
                );
            } else if (store.sidePanel === SidePanelType.Workflow) {
                if (isWorkflowRoute) {
                    openDrawerAndSetContent(
                        <ConvoWorkflowContainer />,
                        true,
                        "Safety Event",
                    );
                }
            } else if (store.sidePanel === SidePanelType.Coaching) {
                openDrawerAndSetContent(
                    <ConvoEvalReview drawerStore={drawerStore} />,
                    false,
                    "Coaching Notes",
                );
            } else {
                drawerStore.setOpen(false);
            }
            return () => {
                drawerStore.closeAndResetDrawer();
            };
        }, [
            store.sidePanel,
            currentRoute,
            convoLabelingStore.selectedLicensedModule?.name,
            drawerStore,
            isWorkflowRoute,
            openDrawerAndSetContent,
        ]);

        const [sampleDisabled, setSampleDisabled] = useState(false);
        const [labelDialogOpen, setLabelDialogOpen] = useState(false);
        const [safetyEventDialogOpen, setSafetyEventDialogOpen] =
            useState(false);
        const [safetyEventLoading, setSafetyEventLoading] = useState(false);

        useEffect(() => {
            convoLabelingStore.getConversationLicensedModules();
        }, [convoLabelingStore]);

        useEffect(() => {
            // fixes an issue where isPlaying would remain true when navigating away from focusedConvo
            // this caused a crash when navigating back into focusedConversation as it would attempt to start playing before peaks init
            // and crash the page
            return () => {
                store.playerOptions.isPlaying = false;
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const conversationTypeName = !store.selectedConversation
            ?.callDurationMillis
            ? "chat"
            : "call";

        const loadingOrFailed =
            store.getTaskLoading(loadConversation) ||
            store.loadConversationFailed;

        const handleCoachButtonClick = async () => {
            await store.openCoachingPanelWithLock({
                refreshCoaching: true,
            });
        };

        const favoriteButton = store.authStore.orgStore.selectedOrganization
            ?.id &&
            store.selectedConversation?.conversationId && (
                <Grid
                    item
                    style={{
                        marginLeft: 0,
                        marginRight: "8px",
                    }}
                >
                    <FavoriteToggle
                        variant="thin"
                        id="favorite-conversation"
                        favoriteFn={() => {
                            if (store.selectedConversationId) {
                                store.addFavorite(store.selectedConversationId);
                            }
                        }}
                        unfavoriteFn={() => {
                            if (store.selectedConversationId) {
                                store.removeFavorite(
                                    store.selectedConversationId,
                                );
                            }
                        }}
                        isFavorited={
                            !!store.selectedConversationId &&
                            store.hasFavorite(store.selectedConversationId)
                        }
                    />
                </Grid>
            );

        const evaluateButton = store.authStore.orgStore.selectedOrganization
            ?.id &&
            canSample &&
            store.selectedConversation?.conversationId && (
                <Grid item>
                    <AcxButton
                        style={{
                            marginLeft: 0,
                            marginRight: "8px",
                        }}
                        disabled={sampleDisabled}
                        onClick={store.toggleEvaluateConversationDialogOpen}
                        color="white"
                    >
                        Evaluate
                        {isLargeScreen && ` this ${conversationTypeName}`}
                    </AcxButton>
                </Grid>
            );

        const labelButton = showLabelButton &&
            store.selectedConversation?.conversationId && (
                <Grid item>
                    <AcxButton
                        id={"label-conversation-button"}
                        style={{
                            marginLeft: 0,
                            marginRight: "8px",
                        }}
                        disabled={labelButtonIsDisabled}
                        onClick={() => {
                            if (!store.selectedConversation?.conversationId)
                                return;
                            // need to make sure we have the latest soundClip data for convo data labelling clip options
                            store.getSoundClipsByAudioMetadataId(
                                store.selectedConversation?.conversationId,
                            );
                            setLabelDialogOpen(true);
                        }}
                        color="white"
                    >
                        Label
                        {isLargeScreen && ` this ${conversationTypeName}`}
                    </AcxButton>
                </Grid>
            );

        const safetyEventButton = showSafetyEventButton &&
            store.selectedConversation?.conversationId && (
                <Grid item>
                    <AcxButton
                        id={"safety-event-review-button"}
                        style={{
                            marginLeft: 0,
                            marginRight: "8px",
                        }}
                        disabled={labelButtonIsDisabled || safetyEventLoading}
                        loading={safetyEventLoading}
                        onClick={async () => {
                            if (!store.selectedConversation?.conversationId)
                                return;
                            setSafetyEventLoading(true);
                            try {
                                store.getSoundClipsByAudioMetadataId(
                                    store.selectedConversation?.conversationId,
                                );

                                if (
                                    convoLabelingStore
                                        .conversationLicensedModules.length ===
                                        0 ||
                                    convoLabelingStore
                                        .conversationSafetyEventLicensedModules
                                        .length === 0
                                )
                                    await convoLabelingStore.getConversationLicensedModules();
                                const seModules =
                                    convoLabelingStore.conversationSafetyEventLicensedModules;

                                if (seModules.length !== 1) {
                                    setSafetyEventDialogOpen(true);
                                    return;
                                }

                                convoLabelingStore.openLabelingDialog();
                                const result =
                                    await convoLabelingStore.openModuleInLabelingDrawer(
                                        seModules[0].id,
                                    );
                                if (result.lockError) {
                                    messageStore.logError(
                                        "Another user is currently reviewing this conversation. Please select another conversation.",
                                    );
                                    return;
                                }
                                if (!result.ok) return;
                                store.setSidePanel(SidePanelType.DataLabeling);
                            } finally {
                                setSafetyEventLoading(false);
                            }
                        }}
                        color="white"
                    >
                        Review safety event
                    </AcxButton>
                </Grid>
            );

        const coachingButtonDisabled =
            store.sidePanel === SidePanelType.Coaching ||
            store.coachingContextLoading ||
            (coachingInDraft && !store.currentUserIsDraftOwner);

        const coachingButtonLoading =
            store.isOpeningCoachingPanel ||
            store.coachingContextLoading ||
            store.evalReviewStore.coachingLockStatus ===
                CoachingLockStatus.Acquiring;

        const coachingButtonDisabledTooltip =
            coachingInDraft && !store.currentUserIsDraftOwner
                ? alreadyInDraftMessage
                : !hasBeenCoached &&
                  (store.isOpeningCoachingPanel ||
                      store.sidePanel === SidePanelType.Coaching ||
                      store.evalReviewStore.coachingLockStatus ===
                          CoachingLockStatus.Acquiring)
                ? "Coaching in progress"
                : "Already coached";

        const coachButton = showCoachButton &&
            store.selectedConversation?.conversationId && (
                <Grid item>
                    <AcxButton
                        id={"coach-conversation-button"}
                        style={{
                            marginLeft: 0,
                            marginRight: "8px",
                        }}
                        onClick={handleCoachButtonClick}
                        disabled={coachingButtonDisabled}
                        loading={coachingButtonLoading}
                        disabledTooltip={coachingButtonDisabledTooltip}
                        color={"white"}
                        startIcon={
                            <FlagRoundedIcon
                                color={
                                    hasBeenCoached ? "secondary" : "disabled"
                                }
                            />
                        }
                    >
                        {hasBeenCoached ? "Coached" : "Coach"}
                    </AcxButton>
                </Grid>
            );

        return (
            <Grid
                xs={12}
                item
                alignContent={loadingOrFailed ? "center" : "flex-start"}
            >
                <Grid style={containerTransitionStyles}>
                    {store.getTaskLoading(loadConversation) ? (
                        <AcxEmptyState size="large" variant="loading" />
                    ) : store.loadConversationFailed ? (
                        <AcxEmptyState size="large" variant="error" />
                    ) : (
                        <>
                            <Grid
                                container
                                item
                                alignItems="center"
                                sx={{ mb: 2 }}
                            >
                                <Grid item style={{ marginRight: "auto" }}>
                                    <Typography variant="h1">
                                        Conversation Details
                                    </Typography>
                                </Grid>
                                {favoriteButton}
                                {evaluateButton}
                                {labelButton}
                                {safetyEventButton}
                                {coachButton}
                                <AgentActionButton
                                    onClick={() => {
                                        if (!store.selectedConversationId)
                                            return;
                                        currentChatStore.showFindSimilarConversations();
                                    }}
                                    sx={(theme) => ({
                                        marginRight: theme.spacing(1),
                                    })}
                                    id="find-similar-conversations"
                                >
                                    Find similar conversations
                                </AgentActionButton>
                            </Grid>
                            {showSoundClipEditor &&
                                (!store.authStore.isUserUltra() &&
                                store.authStore.canUserView(
                                    "Restrict Media",
                                ) ? (
                                    <AcxNoAccessMediaPlayer />
                                ) : (
                                    <Paper
                                        sx={{
                                            width: "100%",
                                            marginBottom: "20px",
                                            borderRadius: 2,
                                            overflow: "hidden",
                                        }}
                                        elevation={0}
                                    >
                                        <TranscriptionLinkedSoundClipEditor
                                            soundClipEditorStore={
                                                store.soundClipEditorStore
                                            }
                                            playerOptions={store.playerOptions}
                                            fullControls={true}
                                            noSegmentRemoval
                                            id={
                                                "focused-conversation-audio-interaction-editor"
                                            }
                                            generateMp3={false}
                                            clip={store.currentClip as any}
                                            editorHeight={88}
                                            enableZoom
                                            onClipGenerated={
                                                store.onAudioClipCreated
                                            }
                                            onClipUpdated={
                                                store.onAudioClipUpdated
                                            }
                                            segmentList={
                                                store.clipsToSegmentList
                                            }
                                        />
                                    </Paper>
                                ))}

                            <FocusedConversationTabs />

                            {/* Evaluate Conversation Confirmation Dialog */}
                            {store.evaluateConversationDialogOpen && (
                                <EvaluateConversationDialog
                                    onConfirmCallback={() => {
                                        setSampleDisabled(true);
                                        store.toggleEvaluationSuccessDialogOpen();
                                    }}
                                />
                            )}

                            {/* Label Conversation Confirmation Dialog */}
                            {labelDialogOpen && (
                                <LabelConversationDialog
                                    title="Label this conversation"
                                    subtitle="Select a module to start labeling this conversation for data insights."
                                    isOpen={labelDialogOpen}
                                    availableModules={
                                        convoLabelingStore.conversationLicensedModules
                                    }
                                    onClose={() => setLabelDialogOpen(false)}
                                />
                            )}

                            {/* Safety Event Review Confirmation Dialog */}
                            {safetyEventDialogOpen && (
                                <LabelConversationDialog
                                    title="Review safety event"
                                    subtitle="Select a safety event review module to start reviewing this conversation."
                                    isOpen={safetyEventDialogOpen}
                                    availableModules={
                                        convoLabelingStore.conversationSafetyEventLicensedModules
                                    }
                                    onClose={() =>
                                        setSafetyEventDialogOpen(false)
                                    }
                                />
                            )}

                            {/* Success Dialog  */}
                            {store.evaluateSuccessDialogOpen && (
                                <SuccessDialog
                                    isOpen={store.evaluateSuccessDialogOpen}
                                    onClose={
                                        store.toggleEvaluationSuccessDialogOpen
                                    }
                                    confirmButtonText="Back to Conversations"
                                    onConfirm={
                                        store.toggleEvaluationSuccessDialogOpen
                                    }
                                    title="Conversations Sampling"
                                    subTitle="You'll be notified when sampling is complete. Reporting data for these evaluations will be available tomorrow."
                                />
                            )}
                        </>
                    )}
                </Grid>
            </Grid>
        );
    },
);

export default FocusedConversation;
